import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/dateUtils";

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending Gallery" },
  { value: "gallery_added", label: "Gallery Added" },
  { value: "delivered", label: "Delivered" },
  { value: "expired", label: "Expired" },
];

const STATUS_COLORS = {
  pending: "bg-soft-gray text-midnight-black/70",
  gallery_added: "bg-stranger-orange text-clean-white",
  delivered: "bg-midnight-black text-clean-white",
  expired: "bg-red-100 text-red-700",
};

export const GalleriesTab = () => {
  const [galleries, setGalleries] = useState(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/admin/galleries", { params: { status: status || undefined } }).then(({ data }) => setGalleries(data));
  }, [status]);

  return (
    <div data-testid="admin-galleries-tab">
      <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            data-testid={`gallery-status-tab-${t.value || "all"}`}
            onClick={() => setStatus(t.value)}
            className={`text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 transition-colors ${status === t.value ? "bg-stranger-orange text-clean-white border-stranger-orange" : "border-soft-gray hover:border-stranger-orange"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {galleries === null ? (
        <p className="font-medium text-midnight-black/60">Loading galleries...</p>
      ) : galleries.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-galleries">No sessions match.</p>
      ) : (
        <div className="space-y-3" data-testid="galleries-list">
          {galleries.map((g) => (
            <div key={g.session_id} className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`gallery-row-${g.session_id}`}>
              <div>
                <p className="font-extrabold text-sm uppercase">{g.session_title}</p>
                <p className="text-xs font-medium text-midnight-black/60">
                  {g.photographer_name} · {formatDate(g.session_date)} · {g.participant_count} participant(s)
                </p>
                {(g.gallery_delivery_date || g.gallery_expiration_date) && (
                  <p className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/40 mt-1">
                    {g.gallery_delivery_date && `Delivery: ${formatDate(g.gallery_delivery_date)}`} {g.gallery_expiration_date && `· Expires: ${formatDate(g.gallery_expiration_date)}`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {g.gallery_url && (
                  <a href={g.gallery_url} target="_blank" rel="noreferrer" data-testid={`gallery-link-${g.session_id}`} className="text-stranger-orange">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[g.gallery_status]}`} data-testid={`gallery-status-${g.session_id}`}>
                  {STATUS_TABS.find((t) => t.value === g.gallery_status)?.label || g.gallery_status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
